import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Cloud, ShieldCheck, Zap, HardDrive, Sparkles, Mail, Check, Gift, Users } from "lucide-react";
import Navbar from "@/components/Navbar";
import ScarcityBanner from "@/components/ScarcityBanner";
import AuthModal from "@/components/AuthModal";
import { Button } from "@/components/ui/button";
import { api, formatBytes } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/I18nContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const PLAN_META = {
  storage_516gb_lifetime: { id: "516gb", label: "516 GB", badge: null,          testid: "pricing-card-516gb", buyTestid: "buy-plan-516gb-button" },
  storage_1tb_lifetime:  { id: "1tb",   label: "1 TB",   badge: "Piu Popolare", testid: "pricing-card-1tb",  buyTestid: "buy-plan-1tb-button" },
  storage_2tb_lifetime:  { id: "2tb",   label: "2 TB",   badge: "Miglior Valore", testid: "pricing-card-2tb", buyTestid: "buy-plan-2tb-button" },
  storage_5tb_lifetime:  { id: "5tb",   label: "5 TB",   badge: "Pro / Power",  testid: "pricing-card-5tb", buyTestid: "buy-plan-5tb-button" },
};

const PLAN_FEATURES_KEYS = {
  "516gb": ["feat_lifetime", "feat_2_devices", "feat_encryption", "feat_standard_support"],
  "1tb":   ["feat_lifetime", "feat_2_devices", "feat_encryption", "feat_photo_backup", "feat_priority_support"],
  "2tb":   ["feat_lifetime", "feat_2_devices", "feat_encryption", "feat_shared_pw", "feat_max_speed"],
  "5tb":   ["feat_lifetime", "feat_2_devices", "feat_zero_knowledge", "feat_versions_365", "feat_vip_support"],
};

const PLAN_FEATURES = {};  // deprecated, kept for reference

export default function Landing() {
  const { user } = useAuth();
  const { t, locale, currency, setCurrency, formatPrice } = useT();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [meta, setMeta] = useState({ licenses_remaining: 500, licenses_sold: 0, max_licenses: 500 });
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [checkoutEmail, setCheckoutEmail] = useState("");
  const [isGift, setIsGift] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [quantities, setQuantities] = useState({});
  const [contact, setContact] = useState({ email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const getQty = (k) => quantities[k] || 1;
  const setQty = (k, v) => setQuantities({ ...quantities, [k]: Math.max(1, Math.min(20, v)) });
  const discountFor = (q) => (q >= 5 ? 20 : q >= 3 ? 15 : q === 2 ? 10 : 0);

  useEffect(() => {
    const load = () => api.get("/plans").then(({ data }) => {
      setPlans(data.plans);
      setMeta({
        licenses_remaining: data.licenses_remaining,
        licenses_sold: data.licenses_sold,
        max_licenses: data.max_licenses,
      });
    }).catch(() => {});
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  const buy = async (lookup_key) => {
    const email = user?.email || checkoutEmail;
    if (!email) {
      toast.error(t("err_email_first"));
      return;
    }
    if (isGift && !recipientEmail) {
      toast.error(t("err_gift_email"));
      return;
    }
    // Check if this email has a verified student status → auto-apply 20% discount
    let student = false;
    try {
      const { data: st } = await api.get(`/student/status?email=${encodeURIComponent(email)}`);
      student = !!st.verified;
    } catch (err) {
      console.warn("[checkout] student status check failed", err?.response?.status || err?.message);
    }
    setBusy(true);
    try {
      const suffixMap = { usd: "_usd", gbp: "_gbp", chf: "_chf", cad: "_cad" };
      const finalKey = currency !== "eur" ? `${lookup_key}${suffixMap[currency] || ""}` : lookup_key;
      const referral_code = localStorage.getItem("nx_ref") || null;
      const { data } = await api.post("/payments/checkout", {
        lookup_key: finalKey,
        email,
        origin_url: window.location.origin,
        is_gift: isGift,
        recipient_email: isGift ? recipientEmail : null,
        locale,
        quantity: getQty(lookup_key),
        referral_code,
        student,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_payment"));
    } finally {
      setBusy(false);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/support/contact", contact);
      toast.success(t("support_ok"));
      setContact({ email: "", subject: "", message: "" });
    } catch {
      toast.error(t("support_err"));
    } finally {
      setBusy(false);
    }
  };

  const openAuth = (m) => { setAuthMode(m); setAuthOpen(true); };

  return (
    <div className="min-h-screen text-slate-100">
      <Navbar onOpenAuth={openAuth} />

      {/* Scarcity banner */}
      <ScarcityBanner remaining={meta.licenses_remaining} total={meta.max_licenses || 500} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-40" />
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-800 bg-slate-900/60 px-3 py-1 text-xs text-slate-300">
                <ShieldCheck className="h-3.5 w-3.5 text-cyan-300" />
                {t("hero_badge")}
              </div>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                {t("hero_title_1")} <br />
                <span className="text-gradient">{t("hero_title_2")}</span>
              </h1>
              <p className="mt-6 max-w-xl text-lg text-slate-300">
                {t("hero_sub")} <strong className="text-slate-100">5 TB</strong> {t("hero_sub_2")}{" "}
                <strong className="text-cyan-300">500 {t("banner_licenses")}</strong> {t("hero_sub_3")}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  data-testid="hero-cta-pricing"
                  className="bg-cyan-400 text-slate-950 hover:bg-cyan-300"
                  onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
                >
                  {t("hero_cta_pricing")}
                </Button>
                <Button
                  variant="outline"
                  data-testid="hero-cta-redeem"
                  onClick={() => nav("/redeem")}
                  className="border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800"
                >
                  {t("hero_cta_redeem")}
                </Button>
              </div>
              <div className="mt-8 flex flex-wrap gap-6 text-sm text-slate-400">
                <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-cyan-300" /> {t("hero_badge_1")}</div>
                <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-cyan-300" /> {t("hero_badge_2")}</div>
                <div className="flex items-center gap-2"><HardDrive className="h-4 w-4 text-cyan-300" /> {t("hero_badge_3")}</div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-cyan-500/20 via-blue-500/10 to-transparent blur-2xl" />
              <div className="glass relative overflow-hidden rounded-2xl p-1">
                <img
                  src="https://images.unsplash.com/photo-1760670399462-f5e479452c27?crop=entropy&cs=srgb&fm=jpg&w=940&q=85"
                  alt="Cloud storage"
                  className="h-[420px] w-full rounded-xl object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="mb-6 rounded-2xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-cyan-500/10 to-amber-500/10 p-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-amber-400 to-cyan-400">
                <Users className="h-5 w-5 text-slate-950" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-amber-300">{locale === "en" ? "New" : "Novita"}</div>
                <div className="text-lg font-bold text-slate-100">{locale === "en" ? "Family Bundle - 4 × 1 TB" : "Bundle Famiglia - 4 × 1 TB"}</div>
                <div className="text-xs text-slate-400">{locale === "en" ? "One click, four codes, 15% off. Perfect gift." : "Un click, quattro codici, 15% di sconto. Regalo perfetto."}</div>
              </div>
            </div>
            <Button
              data-testid="family-bundle-cta"
              onClick={() => nav("/bundle/family")}
              className="bg-gradient-to-r from-amber-400 to-cyan-400 text-slate-950 hover:opacity-90"
            >
              {locale === "en" ? "See bundle" : "Scopri il bundle"} →
            </Button>
          </div>
        </div>
        <div className="mb-10 flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{t("pricing_title_1")} <span className="text-gradient">{t("pricing_title_2")}</span></h2>
            <p className="mt-2 max-w-2xl text-slate-400">{t("pricing_sub")}</p>
          </div>
          <div className="w-full max-w-md">
            <Label htmlFor="checkout-email" className="mb-2 block text-xs uppercase tracking-widest text-slate-400">{t("pricing_email_label")}</Label>
            <Input
              id="checkout-email"
              type="email"
              data-testid="checkout-email-input"
              placeholder={user?.email || "tu@esempio.it"}
              value={user?.email || checkoutEmail}
              onChange={(e) => setCheckoutEmail(e.target.value)}
              disabled={!!user}
              className="border-slate-800 bg-slate-900 text-slate-100"
            />
            <div className="mt-3 flex items-center gap-3 rounded-lg border border-slate-800 bg-slate-900/60 p-3">
              <Gift className="h-4 w-4 text-cyan-300" />
              <div className="flex-1">
                <Label htmlFor="gift-toggle" className="text-sm text-slate-200">{t("gift_label")}</Label>
                <p className="text-[11px] text-slate-500">{t("gift_desc")}</p>
              </div>
              <Switch
                id="gift-toggle"
                data-testid="gift-toggle"
                checked={isGift}
                onCheckedChange={setIsGift}
              />
            </div>
            {isGift && (
              <div className="mt-2">
                <Label htmlFor="gift-email" className="mb-1 block text-xs text-slate-400">{t("gift_recipient")}</Label>
                <Input
                  id="gift-email"
                  type="email"
                  data-testid="gift-recipient-email"
                  placeholder="amico@esempio.it"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  className="border-slate-800 bg-slate-900 text-slate-100"
                />
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-2 lg:grid-cols-4">
          {plans.map((p) => {
            const cardMeta = PLAN_META[p.lookup_key] || {};
            const featureKeys = PLAN_FEATURES_KEYS[cardMeta.id] || [];
            const features = featureKeys.map((k) => t(k));
            const highlight = cardMeta.id === "1tb";
            return (
              <div
                key={p.lookup_key}
                data-testid={cardMeta.testid}
                className={`card-hover relative rounded-2xl border p-6 ${highlight ? "border-cyan-500/60 bg-slate-900/80 glow-cyan" : "border-slate-800 bg-slate-900/50"}`}
              >
                {cardMeta.badge && (
                  <div className={`absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[10px] uppercase tracking-widest ${highlight ? "bg-cyan-400 text-slate-950" : "bg-slate-800 text-cyan-300"}`}>
                    {cardMeta.badge}
                  </div>
                )}
                <div className="flex items-center gap-2 text-slate-300">
                  <HardDrive className="h-4 w-4 text-cyan-300" />
                  <span className="text-sm">{cardMeta.label}</span>
                </div>
                <div className="mt-4 flex items-end gap-1">
                  <span className="text-4xl font-extrabold text-slate-100">{formatPrice(p.prices)}</span>
                  <span className="mb-1 text-sm text-slate-400">{t("price_once")}</span>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <p className="text-xs text-slate-500">{t("price_lifetime")}</p>
                  <span
                    data-testid={`live-counter-${cardMeta.id}`}
                    className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-medium text-cyan-300"
                  >
                    {meta.licenses_remaining ?? 500}/500
                  </span>
                </div>

                <div className="mt-4 flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/60 p-2">
                  <span className="text-xs text-slate-400">{t("qty_label")}</span>
                  <button
                    data-testid={`qty-minus-${cardMeta.id}`}
                    onClick={() => setQty(p.lookup_key, getQty(p.lookup_key) - 1)}
                    className="grid h-6 w-6 place-items-center rounded bg-slate-800 text-slate-100 hover:bg-slate-700"
                    aria-label="minus"
                  >-</button>
                  <span
                    data-testid={`qty-value-${cardMeta.id}`}
                    className="mono min-w-[24px] text-center text-sm text-cyan-300"
                  >{getQty(p.lookup_key)}</span>
                  <button
                    data-testid={`qty-plus-${cardMeta.id}`}
                    onClick={() => setQty(p.lookup_key, getQty(p.lookup_key) + 1)}
                    className="grid h-6 w-6 place-items-center rounded bg-slate-800 text-slate-100 hover:bg-slate-700"
                    aria-label="plus"
                  >+</button>
                  {discountFor(getQty(p.lookup_key)) > 0 && (
                    <span
                      data-testid={`qty-discount-${cardMeta.id}`}
                      className="ml-auto rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-bold text-emerald-300"
                    >-{discountFor(getQty(p.lookup_key))}% {t("qty_discount")}</span>
                  )}
                </div>

                <ul className="mt-5 space-y-2 text-sm">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-slate-300">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" /> {f}
                    </li>
                  ))}
                </ul>

                <Button
                  data-testid={cardMeta.buyTestid}
                  disabled={busy}
                  onClick={() => buy(p.lookup_key)}
                  className={`mt-6 w-full ${highlight ? "bg-cyan-400 text-slate-950 hover:bg-cyan-300" : "bg-slate-800 text-slate-100 hover:bg-slate-700"}`}
                >
                  {t("buy_prefix")} {cardMeta.label}
                </Button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Security */}
      <section id="security" className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="grid gap-6 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
            <ShieldCheck className="h-6 w-6 text-cyan-300" />
            <h3 className="mt-3 text-lg font-semibold">{t("sec_1_t")}</h3>
            <p className="mt-2 text-sm text-slate-400">{t("sec_1_d")}</p>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
            <HardDrive className="h-6 w-6 text-cyan-300" />
            <h3 className="mt-3 text-lg font-semibold">{t("sec_2_t")}</h3>
            <p className="mt-2 text-sm text-slate-400">{t("sec_2_d")}</p>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
            <Zap className="h-6 w-6 text-cyan-300" />
            <h3 className="mt-3 text-lg font-semibold">{t("sec_3_t")}</h3>
            <p className="mt-2 text-sm text-slate-400">{t("sec_3_d")}</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
        <h2 className="text-3xl font-bold tracking-tight">{t("faq_title_1")} <span className="text-gradient">{t("faq_title_2")}</span></h2>
        <div className="mt-8 space-y-4">
          {[
            { q: t("faq_q1"), a: t("faq_a1") },
            { q: t("faq_q2"), a: t("faq_a2") },
            { q: t("faq_q3"), a: t("faq_a3") },
            { q: t("faq_q4"), a: t("faq_a4") },
          ].map((f) => (
            <details key={f.q} className="group rounded-xl border border-slate-800 bg-slate-900/50 p-5">
              <summary className="flex cursor-pointer items-center justify-between text-slate-100">
                <span className="font-medium">{f.q}</span>
                <span className="text-cyan-300 transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-sm text-slate-400">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Support */}
      <section id="support" className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-8">
          <div className="flex items-center gap-3">
            <Mail className="h-6 w-6 text-cyan-300" />
            <h2 className="text-2xl font-bold">{t("support_title_1")} <span className="text-gradient">{t("support_title_2")}</span></h2>
          </div>
          <p className="mt-2 text-sm text-slate-400">
            {t("support_desc_1")}{" "}
            <a
              href="mailto:st.support25@gmail.com"
              data-testid="support-email-link"
              className="text-cyan-300 underline underline-offset-4"
            >
              st.support25@gmail.com
            </a>
            {t("support_desc_2")}
          </p>

          <form onSubmit={submitContact} className="mt-6 grid gap-4 md:grid-cols-2">
            <div>
              <Label className="text-slate-300">{t("support_email")}</Label>
              <Input
                type="email"
                required
                data-testid="support-email-input"
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                className="mt-1 border-slate-800 bg-slate-950 text-slate-100"
              />
            </div>
            <div>
              <Label className="text-slate-300">{t("support_subject")}</Label>
              <Input
                required
                data-testid="support-subject-input"
                value={contact.subject}
                onChange={(e) => setContact({ ...contact, subject: e.target.value })}
                className="mt-1 border-slate-800 bg-slate-950 text-slate-100"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-slate-300">{t("support_message")}</Label>
              <Textarea
                required
                rows={4}
                data-testid="support-message-input"
                value={contact.message}
                onChange={(e) => setContact({ ...contact, message: e.target.value })}
                className="mt-1 border-slate-800 bg-slate-950 text-slate-100"
              />
            </div>
            <div className="md:col-span-2">
              <Button
                type="submit"
                disabled={busy}
                data-testid="support-submit-btn"
                className="bg-cyan-400 text-slate-950 hover:bg-cyan-300"
              >
                {t("support_send")}
              </Button>
            </div>
          </form>
        </div>
      </section>

      <footer className="border-t border-slate-800/60 py-8 text-center text-xs text-slate-500">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-6 gap-y-2 px-4">
          <a href="/business" data-testid="footer-business-link" className="hover:text-cyan-300">{locale === "en" ? "For business" : "Aziende"}</a>
          <a href="/student" data-testid="footer-student-link" className="hover:text-cyan-300">{locale === "en" ? "Student discount" : "Sconto studenti"}</a>
          <a href="/bundle/family" className="hover:text-cyan-300">{locale === "en" ? "Family bundle" : "Bundle famiglia"}</a>
          <a href="#support" className="hover:text-cyan-300">{t("nav_support")}</a>
        </div>
        <div className="mt-4">{t("footer")}</div>
      </footer>

      <AuthModal open={authOpen} mode={authMode} onOpenChange={setAuthOpen} />
    </div>
  );
}
